import { api } from "@redux/api";
import { effectiveUiFormType } from "@/utils/form-type-generic-compat";

function equipmentTypeQueryPart(equipmentType) {
  if (equipmentType === undefined || equipmentType === null) {
    return "";
  }
  if (`${equipmentType}`.trim() === "") {
    return "";
  }
  return "&type=" + encodeURIComponent(equipmentType);
}

function companyQueryPart(companyId) {
  if (companyId === undefined || companyId === null) {
    return "";
  }
  if (`${companyId}`.trim() === "") {
    return "";
  }
  return "&company=" + encodeURIComponent(companyId);
}

/**
 * 与列表接口返回行对齐：在无法带 form_type 查询时，于浏览器侧收紧结果。
 * - form_type 必须一致
 * - type（设备 category）：模板若限定 type，则须与当前设备一致；模板 type 为空表示不限定
 * - company：模板若绑定 company，则须与当前公司一致；company 为空表示全局/未限定
 */
function matchesTemplateScope(row, { formType, equipmentType, companyId }) {
  if (effectiveUiFormType(row) !== formType) {
    return false;
  }
  if (equipmentType != null && `${equipmentType}`.trim() !== "") {
    const rt = row.type;
    if (rt != null && `${rt}`.trim() !== "" && String(rt) !== String(equipmentType)) {
      return false;
    }
  }
  if (companyId != null && `${companyId}`.trim() !== "") {
    const rc = row.company;
    if (rc != null && `${rc}`.trim() !== "" && String(rc) !== String(companyId)) {
      return false;
    }
  }
  return true;
}

/**
 * List form templates for a given Form.form_type, optional equipment category (`type`),
 * and optional `company` (与设施上下文一致，避免拉到其它公司的模板).
 *
 * Some backends return 400 for newer `form_type` values (e.g. `generic`) when django-filter
 * uses a strict ChoiceFilter. In that case we omit `form_type` in the query and filter in the client.
 *
 * @param {object} opts
 * @param {string} opts.formType
 * @param {string|number|null|undefined} [opts.equipmentType] — equipment `category` → query `type`
 * @param {string|number|null|undefined} [opts.companyId] — → query `company`
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export async function filterFormsByFormType({ formType, equipmentType, companyId }) {
  const typePart = equipmentTypeQueryPart(equipmentType);
  const companyPart = companyQueryPart(companyId);
  const paginationPart = "page=1&page_size=999";
  const primaryQs = `form_type=${encodeURIComponent(formType)}&${paginationPart}${typePart}${companyPart}`;

  try {
    return await api.filterForms(primaryQs, { skipGlobalError: true });
  } catch (e) {
    if (e?.response?.status !== 400) {
      throw e;
    }
  }

  const looseQs = `${paginationPart}${typePart}${companyPart}`;
  const response = await api.filterForms(looseQs);
  const rows = response?.data?.results || [];
  const filtered = rows.filter((row) =>
    matchesTemplateScope(row, { formType, equipmentType, companyId })
  );

  return {
    ...response,
    data: {
      ...response.data,
      results: filtered,
      count: filtered.length,
    },
  };
}
